import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, map } from 'rxjs';
import { API_CONFIG, ApiConfig } from '../../api-config';
import { ApiResponse } from '@shared-models';

interface PageableData {
  content: any[];
  page: number;
  size: number;
  totalElements: number;
  totalPages: number;
  first: boolean;
  last: boolean;
  empty: boolean;
}

/**
 * Siglas de identificación por país (flatten areas).
 * Endpoint: `/settings/countries/{countryId}/identification-acronyms`
 */
@Injectable({ providedIn: 'root' })
export class IdentificationAcronymService {
  private readonly httpClient = inject(HttpClient);
  private readonly apiConfig = inject<ApiConfig>(API_CONFIG);

  private get apiUrl(): string {
    return `${this.apiConfig.apiUrl}/settings/countries`;
  }

  private resource(countryId: string, id?: string | number): string {
    const base = `${this.apiUrl}/${countryId}/identification-acronyms`;
    return id !== undefined && id !== null ? `${base}/${id}` : base;
  }

  private normalizeStatus(status: string | undefined): 'active' | 'inactive' {
    if (!status) return 'active';
    return status.toLowerCase() as 'active' | 'inactive';
  }

  private mapItem(it: any): IdentificationAcronym {
    const typeOfTaxpayers: IdentificationAcronymTaxpayer[] = Array.isArray(
      it?.typeOfTaxpayers,
    )
      ? it.typeOfTaxpayers
          .filter((tp: any) => tp?.id != null)
          .map((tp: any) => ({
            id: String(tp.id),
            prefix: tp.prefix || '',
            name: tp.name || tp.prefix || '',
          }))
      : [];

    const typeOfTaxpayerIds =
      typeOfTaxpayers.length > 0
        ? typeOfTaxpayers.map((tp) => tp.id)
        : Array.isArray(it?.typeOfTaxpayerIds)
          ? it.typeOfTaxpayerIds.map((id: unknown) => String(id))
          : [];

    const name = it.name || it.title || '';
    return {
      id: it.id,
      code: it.code || it.prefix || '',
      prefix: it.prefix || '',
      title: name,
      name,
      typeFormatId: it.typeFormatId || '',
      typeFormatName: it.typeFormatName || '',
      min: it.min ?? '',
      max: it.max ?? '',
      format: it.format || '',
      mask: it.mask || '',
      typeOfTaxpayers,
      typeOfTaxpayerIds,
      status: this.normalizeStatus(it.status),
      createdAt: it.createdAt || it.created_at || '',
    };
  }

  private toApiPayload(payload: any): Record<string, unknown> {
    const body: Record<string, unknown> = {
      name: String(payload?.name ?? payload?.title ?? '').trim(),
    };
    const code = String(payload?.code ?? '').trim();
    if (code) body['code'] = code;
    const prefix = String(payload?.prefix ?? '').trim();
    if (prefix) body['prefix'] = prefix;
    if (payload?.status != null && payload.status !== '') {
      body['status'] = String(payload.status).toUpperCase();
    }
    const typeFormatId = String(payload?.typeFormatId ?? '').trim();
    if (typeFormatId) body['typeFormatId'] = typeFormatId;

    const minRaw = payload?.min;
    body['min'] =
      minRaw !== undefined && minRaw !== null && String(minRaw).trim() !== ''
        ? Number(minRaw)
        : 1;
    const maxRaw = payload?.max;
    body['max'] =
      maxRaw !== undefined && maxRaw !== null && String(maxRaw).trim() !== ''
        ? Number(maxRaw)
        : 1;
    if (payload?.format !== undefined) {
      body['format'] = String(payload.format ?? '').trim() || null;
    }
    if (payload?.mask !== undefined) {
      body['mask'] = String(payload.mask ?? '').trim() || null;
    }
    if (payload?.typeOfTaxpayerIds !== undefined) {
      body['typeOfTaxpayerIds'] = Array.isArray(payload.typeOfTaxpayerIds)
        ? payload.typeOfTaxpayerIds
        : [];
    }
    return body;
  }

  getIdentificationAcronyms(
    countryId: string,
    page = 0,
    size = 20,
    sort = 'createdAt',
    direction: 'asc' | 'desc' = 'desc',
    search?: string,
    status?: string,
  ): Observable<IdentificationAcronymPageResponse> {
    let params = new HttpParams()
      .set('page', page.toString())
      .set('size', size.toString())
      .set('sort', sort)
      .set('direction', direction);

    if (search && search.trim()) {
      params = params.set('search', search.trim());
    }
    if (status && status.trim()) {
      params = params.set('status', status.trim().toUpperCase());
    }

    return this.httpClient
      .get<ApiResponse<PageableData>>(this.resource(countryId), { params })
      .pipe(
        map((response) => {
          const pageableData = response.data;
          const items = pageableData?.content || [];
          return {
            items: items.map((it: any) => this.mapItem(it)),
            page: pageableData?.page ?? 0,
            size: pageableData?.size ?? size,
            totalElements: pageableData?.totalElements ?? 0,
            totalPages: pageableData?.totalPages ?? 0,
          } as IdentificationAcronymPageResponse;
        }),
      );
  }

  getIdentificationAcronymById(countryId: string, id: string | number) {
    return this.httpClient
      .get<ApiResponse<any>>(this.resource(countryId, id))
      .pipe(map((resp) => this.mapItem(resp.data)));
  }

  createIdentificationAcronym(countryId: string, payload: any) {
    return this.httpClient
      .post<{ success: boolean; message: string }>(
        this.resource(countryId),
        this.toApiPayload(payload),
      )
      .pipe(
        map((res) => ({
          message: res.message || 'Acrónimo de identificación creado',
        })),
      );
  }

  updateIdentificationAcronym(
    countryId: string,
    id: string | number,
    payload: any,
  ) {
    return this.httpClient
      .put<{ success: boolean; message: string }>(
        this.resource(countryId, id),
        this.toApiPayload(payload),
      )
      .pipe(
        map((res) => ({
          message: res.message || 'Acrónimo de identificación actualizado',
        })),
      );
  }

  deleteIdentificationAcronym(countryId: string, id: string | number) {
    return this.httpClient
      .delete<{ success: boolean; message: string }>(
        this.resource(countryId, id),
      )
      .pipe(
        map((res) => ({
          message: res.message || 'Acrónimo de identificación eliminado',
        })),
      );
  }
}

export interface IdentificationAcronymTaxpayer {
  id: string;
  prefix?: string;
  name?: string;
}

export interface IdentificationAcronym {
  id: string;
  code: string;
  prefix?: string;
  title: string;
  name?: string;
  typeFormatId?: string;
  typeFormatName?: string;
  min?: string | number;
  max?: string | number;
  format?: string;
  mask?: string;
  typeOfTaxpayers?: IdentificationAcronymTaxpayer[];
  typeOfTaxpayerIds?: string[];
  status?: string;
  createdAt?: string;
}

export interface IdentificationAcronymPageResponse {
  items: IdentificationAcronym[];
  page: number;
  size: number;
  totalElements: number;
  totalPages: number;
}
